/**
 * Automatic role mapping — the rules that decide what role a new SSO sign-in gets.
 *
 * Without this, every new person arrives as an SA and somebody has to notice. With it,
 * an admin writes the decision down once ("anyone @partner.example is an Account
 * Manager") and it applies from their first request.
 *
 * A role an admin sets by hand always wins and is never overwritten here — that is
 * enforced server-side by `role_source`, and said out loud on the card so nobody has to
 * take it on trust.
 */

import { Plus, Trash2, Wand2 } from "lucide-react";
import { useState } from "react";
import { DataTable } from "@/components/shared/data-table";
import { EmptyState } from "@/components/shared/empty-state";
import { ErrorState } from "@/components/shared/error-state";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  isUserRole,
  USER_ROLES,
  useCreateRoleRule,
  useDeleteRoleRule,
  useRoleRules,
  useUpdateRoleRule,
} from "@/hooks/use-users";

export function RoleRuleCard() {
  const { data: rules = [], isLoading, isError, error, refetch } = useRoleRules();
  const createRule = useCreateRoleRule();
  const updateRule = useUpdateRoleRule();
  const deleteRule = useDeleteRoleRule();

  const [matchType, setMatchType] = useState<"email" | "domain">("domain");
  const [matchValue, setMatchValue] = useState("");
  const [role, setRole] = useState("ACCT");

  function handleAdd() {
    if (!matchValue.trim() || !isUserRole(role)) return;
    createRule.mutate(
      {
        matchType,
        matchValue: matchValue.trim(),
        role,
        priority: 100,
        active: true,
      },
      { onSuccess: () => setMatchValue("") },
    );
  }

  return (
    <section className="space-y-4">
      <header>
        <h2 className="font-display text-lg font-semibold">Automatic role mapping</h2>
        <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
          What role someone gets the first time they sign in with Microsoft. A role an admin sets by
          hand always wins — these rules never overwrite one.
        </p>
      </header>

      <div className="flex flex-wrap items-end gap-2">
        <div className="space-y-2">
          <Label htmlFor="rule-type">Match</Label>
          <Select
            value={matchType}
            onValueChange={(value) => setMatchType(value === "email" ? "email" : "domain")}
          >
            <SelectTrigger id="rule-type" className="w-36">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="domain">Email domain</SelectItem>
              <SelectItem value="email">Exact email</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="min-w-56 flex-1 space-y-2">
          <Label htmlFor="rule-value">{matchType === "domain" ? "Domain" : "Email address"}</Label>
          <Input
            id="rule-value"
            value={matchValue}
            onChange={(event) => setMatchValue(event.target.value)}
            placeholder={matchType === "domain" ? "nymbl.app" : "person@nymbl.app"}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="rule-role">Role</Label>
          <Select value={role} onValueChange={setRole}>
            <SelectTrigger id="rule-role" className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {USER_ROLES.map((r) => (
                <SelectItem key={r} value={r}>
                  {r}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <Button onClick={handleAdd} disabled={!matchValue.trim() || createRule.isPending}>
          <Plus className="mr-2 h-4 w-4" />
          Add rule
        </Button>
      </div>

      {isError ? (
        <ErrorState message={error?.message ?? "Failed to load rules"} onRetry={() => refetch()} />
      ) : (
        <DataTable
          headers={[
            { label: "Match" },
            { label: "Role" },
            { label: "Status" },
            { label: "", className: "text-right" },
          ]}
          loading={isLoading}
          empty={
            <EmptyState
              icon={Wand2}
              title="No rules yet"
              description="Without a rule, everyone who signs in for the first time starts as SA."
            />
          }
        >
          {rules.map((rule) => (
            <tr key={rule.id} className="border-b border-border last:border-0">
              <td className="px-4 py-3">
                <span className="font-medium">{rule.matchValue}</span>
                <span className="ml-2 text-xs text-muted-foreground">
                  {rule.matchType === "email" ? "exact email" : "domain"}
                </span>
              </td>
              <td className="px-4 py-3">
                <Badge variant="secondary">{rule.role}</Badge>
              </td>
              <td className="px-4 py-3">
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={updateRule.isPending}
                  onClick={() => updateRule.mutate({ id: rule.id, active: !rule.active })}
                >
                  {rule.active ? "Active" : "Paused"}
                </Button>
              </td>
              <td className="px-4 py-3 text-right">
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={`Remove the rule for ${rule.matchValue}`}
                  disabled={deleteRule.isPending}
                  onClick={() => deleteRule.mutate({ id: rule.id })}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </td>
            </tr>
          ))}
        </DataTable>
      )}

      {rules.length > 0 && (
        <p className="text-xs text-muted-foreground">
          An exact email rule beats a domain rule. Removing a rule does not change anyone's current
          role.
        </p>
      )}
    </section>
  );
}
